import { isLiteTemplate } from "@lexicanon/shared"
import { useLingui } from "@lingui/react"
import { Plural, Trans } from "@lingui/react/macro"
import { Link } from "@tanstack/react-router"
import { useMemo } from "react"
import { canReprocess } from "../../lib/meeting-reprocess"
import { displayMeetingTitle } from "../../lib/meeting-title"
import { rosterSpeakerColorMap } from "../../lib/speaker-colors"
import { AvatarStack } from "../AvatarStack"
import {
  formatShortDate,
  formatTime,
  isNamedSpeaker,
  meetingErrorReason,
  speakerFirstName,
  stageLabel,
} from "../MeetingCard/helpers"
import { MeetingCardKebab } from "../MeetingCardKebab"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

const NAMED_SPEAKER_LABELS = 3

/**
 * One meeting in the compact list. Same destination and kebab as the card:
 * the row is a link, and the menu swallows its own clicks so it does not
 * navigate.
 */
export const MeetingListRow = ({
  meeting,
  variant = "full",
  showDate,
  canManage,
  canDeletePermanently,
  onAction,
  processing,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const speakerColors = useMemo(() => rosterSpeakerColorMap(meeting.speaker_names ?? []), [meeting.speaker_names])
  const recordedLite = isLiteTemplate(meeting.template_id)
  const speakers = meeting.speaker_names ?? []
  const namedSpeakers = speakers.filter(isNamedSpeaker).slice(0, NAMED_SPEAKER_LABELS)
  const time = formatTime(i18n, meeting.start_time)
  const date = formatShortDate(i18n, meeting.start_time)
  const when = [
    showDate ? date : null,
    time,
    meeting.duration_minutes && meeting.duration_minutes > 0 ? `${meeting.duration_minutes}m` : null,
  ]
    .filter((part): part is string => Boolean(part))
    .join(" · ")
  const errorReason = meeting.status === "error" ? meetingErrorReason(meeting, i18n) : null
  const processingLabel = processing ? stageLabel(processing.stage, i18n) : null

  return (
    <Link
      to="/meetings/$id"
      params={{ id: meeting.id }}
      className={`${classes.row} lex-surface-card ${meeting.hidden ? classes.rowHidden : ""}`}
      data-testid="meeting-row"
      data-list-variant={variant}
      data-recorded-lite={recordedLite ? "true" : undefined}
    >
      <span className={classes.title}>
        {displayMeetingTitle(meeting.subject, meeting.topics, i18n, {
          lite: recordedLite,
          startedAt: meeting.start_time,
        })}
      </span>
      {when.length > 0 ? <span className={classes.meta}>{when}</span> : null}
      {processing ? (
        <span className={classes.meta}>{processingLabel ?? <Trans>Finishing</Trans>}</span>
      ) : errorReason ? (
        <span className={classes.reason} title={errorReason}>
          {errorReason}
        </span>
      ) : recordedLite ? (
        <span className={classes.meta}>
          <Trans>Transcript</Trans>
        </span>
      ) : meeting.action_count !== undefined ? (
        <span className={classes.meta}>
          <Plural value={meeting.action_count} one="# action" other="# actions" />
        </span>
      ) : null}
      {speakers.length > 0 ? (
        <span className={classes.people}>
          <AvatarStack names={speakers} colorMap={speakerColors} />
          {namedSpeakers.length > 0 ? (
            <span className={classes.names} data-testid="speaker-names">
              {namedSpeakers.map(speakerFirstName).join(", ")}
            </span>
          ) : null}
        </span>
      ) : null}
      <span className={classes.kebab}>
        <MeetingCardKebab
          hidden={meeting.hidden}
          canManage={canManage}
          canDeletePermanently={canDeletePermanently}
          canReprocess={canReprocess(meeting.status)}
          variant={recordedLite ? "lite" : "full"}
          onAction={(action) => onAction(meeting.id, action)}
        />
      </span>
    </Link>
  )
}
